/**
 * ConsentSignSection — wiederverwendbarer Einverständnis-Block (Kryo/EMS):
 * Kontraindikations-Checkliste + Erklärung + Unterschrift (Canvas).
 * Meldet via onChange ein fertiges Sign-Payload (oder null solange unvollständig).
 * Genutzt in: ConsentForms (Portal), BookingCalendar (Portal), PublicBooking, AddonServicePopup.
 */
import React, { useState, useEffect, useRef } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Eraser } from 'lucide-react';
import { et, etp } from '../../i18n/embedLang';

export const SignaturePad = ({ onChange }) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const hasDrawn = useRef(false);

  // Canvas wird per CSS skaliert (w-full) → Klick-Koordinaten auf interne Auflösung umrechnen
  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) * (canvas.width / rect.width),
      y: (e.clientY - rect.top) * (canvas.height / rect.height),
    };
  };

  const commit = () => {
    if (hasDrawn.current && canvasRef.current) onChange(canvasRef.current.toDataURL('image/png'));
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    // Capture: pointerup kommt auch an, wenn der Finger den Canvas verlässt (iOS!)
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* noop */ }
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getPos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    ctx.strokeStyle = '#FBBF24';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    const { x, y } = getPos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    hasDrawn.current = true;
  };

  // Auch bei pointercancel (iOS bricht Gesten gern ab) die Signatur übernehmen
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    commit();
  };

  const clear = () => {
    const canvas = canvasRef.current;
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    hasDrawn.current = false;
    onChange(null);
  };

  return (
    <div>
      <canvas
        ref={canvasRef}
        width={560}
        height={160}
        className="w-full rounded-lg border-2 border-dashed border-yellow-500/40 bg-white/5 touch-none"
        style={{ touchAction: 'none' }}
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerCancel={end}
        onPointerLeave={end}
        data-testid="signature-canvas"
      />
      <div className="flex justify-between items-center mt-1">
        <p className="text-xs text-white/40">{et('Hier mit Finger oder Maus unterschreiben')}</p>
        <button type="button" onClick={clear} className="text-xs text-white/50 hover:text-white flex items-center gap-1" data-testid="signature-clear-btn">
          <Eraser className="w-3 h-3" /> {et('Löschen')}
        </button>
      </div>
    </div>
  );
};

const CHECKBOX_CLS = "mt-0.5 h-5 w-5 shrink-0 border-2 border-white/60 bg-white/10 data-[state=checked]:bg-yellow-500 data-[state=checked]:border-yellow-500 data-[state=checked]:text-black";

const ConsentSignSection = ({ form, onChange }) => {
  const [checked, setChecked] = useState([]);
  const [noneApply, setNoneApply] = useState(false);
  const [declAccepted, setDeclAccepted] = useState(false);
  const [signature, setSignature] = useState(null);

  useEffect(() => {
    setChecked([]);
    setNoneApply(false);
    setDeclAccepted(false);
    setSignature(null);
  }, [form?.id]);

  useEffect(() => {
    const hasContra = (form?.contraindications || []).length > 0;
    const valid = form && declAccepted && signature && (!hasContra || noneApply || checked.length > 0);
    onChange(valid ? {
      form_id: form.id,
      checked_contraindications: checked,
      none_apply: noneApply,
      declaration_accepted: true,
      signature_data: signature,
    } : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checked, noneApply, declAccepted, signature]);

  const toggleItem = (item) => {
    setNoneApply(false);
    setChecked(prev => prev.includes(item) ? prev.filter(i => i !== item) : [...prev, item]);
  };

  if (!form) return null;

  return (
    <div className="space-y-3" data-testid="consent-sign-section">
      <p className="text-sm text-white/70">{form.intro}</p>

      {(form.contraindications || []).length > 0 && (
      <div className="space-y-1.5">
        <p className="text-sm font-semibold text-amber-400">{et('Kontraindikationen — bitte alle zutreffenden ankreuzen:')}</p>
        {(form.contraindications || []).map(item => (
          <label key={item} className="flex items-start gap-2 text-sm text-white/80 cursor-pointer hover:bg-white/5 rounded px-2 py-1">
            <Checkbox checked={checked.includes(item)} onCheckedChange={() => toggleItem(item)} className={CHECKBOX_CLS} />
            <span>{item}</span>
          </label>
        ))}
        <label className="flex items-start gap-2 text-sm font-medium text-green-400 cursor-pointer hover:bg-white/5 rounded px-2 py-1.5 border-t border-white/10 mt-2 pt-2">
          <Checkbox checked={noneApply} onCheckedChange={(v) => { setNoneApply(!!v); if (v) setChecked([]); }} className={CHECKBOX_CLS} data-testid="consent-none-apply" />
          <span>{et('Nichts davon trifft auf mich zu')}</span>
        </label>
      </div>
      )}

      {checked.length > 0 && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-sm text-red-300">
          {etp('Du hast {0} Punkt(e) angekreuzt — bitte sprich vor der Anwendung zwingend mit unserem Team bzw. deinem Arzt.', [checked.length])}
        </div>
      )}

      {form.notes && <p className="text-xs text-white/50">{form.notes}</p>}

      <label className="flex items-start gap-2 text-sm text-white/90 cursor-pointer bg-white/5 rounded-lg p-3 border border-white/10">
        <Checkbox checked={declAccepted} onCheckedChange={(v) => setDeclAccepted(!!v)} className={CHECKBOX_CLS} data-testid="consent-declaration-checkbox" />
        <span>{form.declaration}</span>
      </label>

      <SignaturePad onChange={setSignature} />
    </div>
  );
};

export default ConsentSignSection;
